import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/shared/AuthShell";
import { useNeutralAuthCover } from "@/hooks/useNeutralAuthCover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import {
  sendMyVerificationEmail,
  verifyMyEmail,
} from "@/services/authService";
import { Loader2 } from "lucide-react";

const RESEND_COOLDOWN_SECONDS = 30;

export function VerifyEmailPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") ?? "/";
  const { user, refreshUser } = useAuth();
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cover = useNeutralAuthCover();
  const hasSentRef = useRef(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const handleSendCode = useCallback(async (): Promise<void> => {
    setSending(true);
    try {
      await sendMyVerificationEmail(i18n.resolvedLanguage ?? i18n.language);
      toast.success(t("auth.verifyEmail.codeSent"));
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      toast.error(t("auth.verifyEmail.sendError"));
    } finally {
      setSending(false);
    }
  }, [i18n, t]);

  // Auto-send verification email on mount
  useEffect(() => {
    if (hasSentRef.current) return;
    if (!user || user.emailVerifiedAt) return;
    hasSentRef.current = true;
    void handleSendCode();
  }, [user, handleSendCode]);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = async (): Promise<void> => {
    const trimmed = code.trim();
    if (!trimmed) {
      toast.error(t("auth.verifyEmail.codeRequired"));
      return;
    }
    setVerifying(true);
    try {
      await verifyMyEmail(trimmed);
      await refreshUser();
      toast.success(t("auth.verifyEmail.success"));
      const path = returnTo.startsWith("/") ? returnTo : `/${returnTo}`;
      navigate(path !== "/verify-email" ? path : "/", { replace: true });
    } catch {
      toast.error(t("auth.verifyEmail.verifyError"));
    } finally {
      setVerifying(false);
    }
  };

  const resolvedReturnTo = returnTo.startsWith("/") ? returnTo : `/${returnTo}`;
  if (user?.emailVerifiedAt && resolvedReturnTo !== "/verify-email") {
    return <Navigate to={resolvedReturnTo} replace />;
  }

  return (
    <AuthShell cover={cover}>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t("auth.verifyEmail.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("auth.verifyEmail.autoSentHint", {
              email: user?.email ?? "",
            })}
          </p>
        </div>

        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!verifying && code.trim().length >= 4) {
              void handleVerify();
            }
          }}
        >
          <Label htmlFor="code">{t("auth.verifyEmail.codeLabel")}</Label>
          <Input
            ref={codeInputRef}
            id="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="000000"
            maxLength={8}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="font-mono text-center text-lg tracking-widest"
          />
          <Button
            type="submit"
            disabled={verifying || code.trim().length < 4}
            className="w-full"
          >
            {verifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("auth.verifyEmail.verifying")}
              </>
            ) : (
              t("auth.verifyEmail.verifyButton")
            )}
          </Button>
        </form>

        <div className="text-center">
          {cooldown > 0 ? (
            <p className="text-xs text-muted-foreground">
              {t("auth.verifyEmail.resendIn", { seconds: cooldown })}
            </p>
          ) : (
            <button
              type="button"
              onClick={handleSendCode}
              disabled={sending}
              className="text-xs text-primary hover:underline disabled:opacity-50"
            >
              {sending ? t("auth.verifyEmail.sending") : t("auth.verifyEmail.resend")}
            </button>
          )}
        </div>
      </div>
    </AuthShell>
  );
}
